import { useState, useRef, useEffect } from "react";
import { SendHorizonal, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { fileToDataUrl } from "@/lib/api";

export const Composer = ({ onSend, disabled, modelName, isImage, allowReference }) => {
  const [value, setValue] = useState("");
  const [reference, setReference] = useState(null);
  const ref = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 220) + "px";
  }, [value]);

  useEffect(() => { if (!allowReference) setReference(null); }, [allowReference]);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text, reference);
    setValue("");
    setReference(null);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be under 10MB");
    setReference(await fileToDataUrl(file));
  };

  const placeholder = !modelName ? "Loading…"
    : reference ? `Describe how ${modelName} should modify this photo…`
    : isImage ? `Describe the photo you want ${modelName} to render…`
    : `Message ${modelName}… (Enter to send, Shift+Enter for newline)`;

  return (
    <div className="shrink-0 border-t border-slate-800/60 bg-[#090A0F]/90 backdrop-blur-xl">
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-4">
        {reference && (
          <div className="mb-2 inline-flex items-center gap-3 p-2 pr-3 rounded-xl bg-[#11131E] border border-indigo-500/30" data-testid="reference-preview">
            <img src={reference} alt="Reference" className="w-14 h-14 rounded-lg object-cover" />
            <div className="text-xs">
              <p className="text-slate-200 font-medium">Reference photo attached</p>
              <p className="text-slate-500">Subject will be preserved</p>
            </div>
            <button onClick={() => setReference(null)} data-testid="remove-reference-btn" className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-[#1F2338]" aria-label="Remove reference"><X className="w-4 h-4" /></button>
          </div>
        )}
        <div className={`flex items-end gap-2 rounded-2xl border bg-[#11131E] px-3 py-2.5 transition-[border-color,box-shadow] ${disabled ? "border-slate-800/60" : "border-slate-800 focus-within:border-indigo-500/60 focus-within:shadow-[0_0_20px_rgba(99,102,241,0.15)]"}`}>
          {allowReference && (
            <>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="reference-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={disabled} data-testid="attach-reference-btn" className="shrink-0 w-9 h-9 rounded-xl text-slate-400 hover:text-indigo-300 hover:bg-[#1F2338] disabled:opacity-50 flex items-center justify-center transition-colors" aria-label="Attach reference photo" title="Attach a reference photo to edit">
                <ImagePlus className="w-4 h-4" />
              </button>
            </>
          )}
          <textarea
            ref={ref}
            data-testid="chat-input"
            rows={1}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKey}
            disabled={disabled}
            placeholder={placeholder}
            className="flex-1 bg-transparent resize-none outline-none text-[15px] text-slate-100 placeholder:text-slate-600 py-1.5 px-1 max-h-[220px] disabled:opacity-60"
          />
          <button data-testid="send-message-button" onClick={submit} disabled={disabled || !value.trim()} className="shrink-0 w-9 h-9 rounded-xl bg-indigo-500 hover:bg-indigo-600 disabled:bg-[#1F2338] disabled:text-slate-500 text-white flex items-center justify-center transition-colors" aria-label="Send">
            <SendHorizonal className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-slate-600 mt-2 text-center">
          {isImage ? "Images are shown once — pin the ones you love to save them to your gallery." : "Models can make mistakes. Verify important information."}
        </p>
      </div>
    </div>
  );
};
